import { useMemo, useState } from 'react';
import { Transforms } from 'slate';
import { Spinner } from 'app/components';
import { DefaultTable } from 'app/utils/table';
import { ReactEditor, useSlate } from 'slate-react';
import { useEditorSelector } from 'app/state/contexts/EditorContext';
import { useHistory } from 'react-router-dom';
import { showOriginalValueReferenceTable } from '../utils';
import { usePermissions } from 'app/utils/hooks/document-permission';
import { i18n } from 'app/utils/i18n';
import { DeleteBlockModal } from './DeleteBlockModal';

export const ReferenceTable = (props) => {
  const editor = useSlate();
  const { attributes, element, children, diff, side, readOnly } = props;
  const references = useEditorSelector((editor) => editor.references);
  const locale = useEditorSelector((editor) => editor.locale);
  const history = useHistory();
  const pDocument = useEditorSelector((editor) => editor.pDocument);
  const section = useEditorSelector((editor) => editor.section);
  const showOriginalValue = useMemo(() => {
    return showOriginalValueReferenceTable(pDocument, section, element);
  }, [pDocument, section, element]);
  const { documentPermissions } = usePermissions(pDocument.superId);
  const content = useMemo(() => {
    return references?.find((r) => r.superId === element.referenceDataSuperId);
  }, [references, element]);

  const elementId = useMemo(() => `xrp-tableid-${element.uuid}`, [element.uuid]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const tableCssClasses = useMemo(() => {
    const refEntry = pDocument?.content?.referenceData?.find((r) => r.superId === element.referenceDataSuperId);
    const cssClass = refEntry?.cssClass;
    if (!cssClass) return '';
    return cssClass
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean)
      .join(' ');
  }, [pDocument, element.referenceDataSuperId]);
  if (references?.loading) {
    return (
      <div {...attributes}>
        <div style={{ userSelect: 'none' }} contentEditable={false}>
          <Spinner />
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div {...attributes}>
        <div style={{ userSelect: 'none' }} contentEditable={false}>
          No data!
        </div>
      </div>
    );
  }

  const performDelete = () => {
    const path = ReactEditor.findPath(editor, props.element);
    Transforms.select(editor, path);
    Transforms.removeNodes(editor, { at: path });
  };
  const confirmDelete = () => {
    setShowDeleteModal(false);
    performDelete();
  };
  const navigateToReferenceData = () => {
    const prefix = history.location.pathname.split('/sections')[0];
    history.replace(`${prefix}/reference-data/${element.referenceDataSuperId}`);
  };
  const metadata = {
    'data-element-super-id': element.referenceDataSuperId,
    'data-document-revision-id': content.revisionId,
    'data-element-revision-id': `${element.referenceDataSuperId}_${content.revisionId}`,
  };

  return (
    <div className="editor-table--container" {...attributes}>
      {!readOnly && (
        <div className="controls--container">
          <button onClick={() => setShowDeleteModal(true)}>
            <i className="fa-regular fa-trash-can" />
          </button>
          {documentPermissions.includes('DB') && (
            <button onClick={navigateToReferenceData}>
              <i className="fa-regular fa-table" />
            </button>
          )}
        </div>
      )}

      <DeleteBlockModal
        show={showDeleteModal}
        message={i18n('block.delete.modal.table')}
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteModal(false)}
      />

      <div {...metadata}>
        <DefaultTable id={elementId} data={content} locale={locale} showOriginalValue={showOriginalValue} diff={diff} side={side} cssClass={tableCssClasses} />
        {children}
      </div>
    </div>
  );
};
